import axios, { AxiosError } from "axios";
import { api } from "@source/api/http";
import ReqError from "@source/api/reqError";
import {
  ConfirmConsentDocumentPdfRequest,
  ConsentCategory,
  ConsentCategoryListResponse,
  ConsentCategoryQuery,
  ConsentDocument,
  ConsentDocumentListResponse,
  ConsentDocumentPreview,
  ConsentDocumentQuery,
  ConsentPdfUploadTarget,
  ConsentTemplate,
  ConsentTemplateListResponse,
  ConsentTemplateQuery,
  CopyConsentTemplateRequest,
  CreateConsentCategoryRequest,
  CreateConsentDocumentRequest,
  CreateConsentTemplateRequest,
  EmployeeSignRequest,
  ResidentConsentRequest,
  ResidentIdentityVerificationRequest,
  UpdateConsentCategoryRequest,
  UpdateConsentDocumentRequest,
  UpdateConsentTemplateRequest,
} from "@source/api/models/consentModels";
import {normalizeConsentPreviewContentType} from "@source/api/models/consentHelpers";

interface ConsentErrorPayload {
  message?: string | string[];
  statusCode?: number;
}

const isolatedUploadClient = axios.create({
  withCredentials: false,
});

const translateConsentError = (error: unknown): Error => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error : new Error("Unknown consent request error");
  }
  const axiosError = error as AxiosError<ConsentErrorPayload>;
  const downError = ReqError.handleServerDownError(axiosError);
  if (downError) return downError;

  const payload = axiosError.response?.data;
  const payloadMessage = payload?.message;
  const normalizedMessage = Array.isArray(payloadMessage)
    ? payloadMessage.join(". ")
    : typeof payloadMessage === "string" && payloadMessage.length > 0
      ? payloadMessage
      : "";
  const message = normalizedMessage || axiosError.message || "Consent request failed";
  return new ReqError(payload?.statusCode || axiosError.response?.status || 500, message);
};

const request = async <T>(callback: () => Promise<T>): Promise<T> => {
  try {
    return await callback();
  } catch (error: unknown) {
    throw translateConsentError(error);
  }
};

export type ConsentPdfUploadBody = Blob | ArrayBuffer | Uint8Array;

export default class ConsentService {
  static listCategories(organizationId: string, query: ConsentCategoryQuery = {}) {
    return request(async () =>
      (await api.get<ConsentCategoryListResponse>(
        `organizations/${organizationId}/consent-categories`,
        {params: query},
      )).data
    );
  }

  static getCategory(organizationId: string, categoryId: string) {
    return request(async () =>
      (await api.get<ConsentCategory>(
        `organizations/${organizationId}/consent-categories/${categoryId}`,
      )).data
    );
  }

  static createCategory(organizationId: string, body: CreateConsentCategoryRequest) {
    return request(async () =>
      (await api.post<ConsentCategory>(
        `organizations/${organizationId}/consent-categories`,
        body,
      )).data
    );
  }

  static updateCategory(
    organizationId: string,
    categoryId: string,
    body: UpdateConsentCategoryRequest,
  ) {
    return request(async () =>
      (await api.patch<ConsentCategory>(
        `organizations/${organizationId}/consent-categories/${categoryId}`,
        body,
      )).data
    );
  }

  static archiveCategory(organizationId: string, categoryId: string) {
    return request(async () =>
      (await api.post<ConsentCategory>(
        `organizations/${organizationId}/consent-categories/${categoryId}/archive`,
      )).data
    );
  }

  static listTemplates(organizationId: string, query: ConsentTemplateQuery = {}) {
    return request(async () =>
      (await api.get<ConsentTemplateListResponse>(
        `organizations/${organizationId}/consent-templates`,
        {params: query},
      )).data
    );
  }

  static getTemplate(organizationId: string, templateId: string) {
    return request(async () =>
      (await api.get<ConsentTemplate>(
        `organizations/${organizationId}/consent-templates/${templateId}`,
      )).data
    );
  }

  static createTemplate(organizationId: string, body: CreateConsentTemplateRequest) {
    return request(async () =>
      (await api.post<ConsentTemplate>(
        `organizations/${organizationId}/consent-templates`,
        body,
      )).data
    );
  }

  static updateTemplate(
    organizationId: string,
    templateId: string,
    body: UpdateConsentTemplateRequest,
  ) {
    return request(async () =>
      (await api.patch<ConsentTemplate>(
        `organizations/${organizationId}/consent-templates/${templateId}`,
        body,
      )).data
    );
  }

  static copyTemplate(
    organizationId: string,
    templateId: string,
    body: CopyConsentTemplateRequest,
  ) {
    return request(async () =>
      (await api.post<ConsentTemplate>(
        `organizations/${organizationId}/consent-templates/${templateId}/copy`,
        body,
      )).data
    );
  }

  static archiveTemplate(organizationId: string, templateId: string) {
    return request(async () =>
      (await api.post<ConsentTemplate>(
        `organizations/${organizationId}/consent-templates/${templateId}/archive`,
      )).data
    );
  }

  static listDocuments(nursingHomeId: string, query: ConsentDocumentQuery = {}) {
    return request(async () =>
      (await api.get<ConsentDocumentListResponse>(
        `nursing-homes/${nursingHomeId}/consent-documents`,
        {params: query},
      )).data
    );
  }

  static getDocument(nursingHomeId: string, documentId: string) {
    return request(async () =>
      (await api.get<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}`,
      )).data
    );
  }

  static createDocument(nursingHomeId: string, body: CreateConsentDocumentRequest) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents`,
        body,
      )).data
    );
  }

  static updateDocument(
    nursingHomeId: string,
    documentId: string,
    body: UpdateConsentDocumentRequest,
  ) {
    return request(async () =>
      (await api.patch<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}`,
        body,
      )).data
    );
  }

  static sendDocument(nursingHomeId: string, documentId: string) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}/send`,
      )).data
    );
  }

  static createPdfUploadUrl(nursingHomeId: string, documentId: string) {
    return request(async () =>
      (await api.post<ConsentPdfUploadTarget>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}/pdf/upload-url`,
      )).data
    );
  }

  /**
   * Uploads directly to the presigned URL through an isolated Axios instance.
   * The configured API base URL, Authorization header and interceptors are not used.
   */
  static uploadPdf(uploadUrl: string, body: ConsentPdfUploadBody): Promise<void> {
    return request(async () => {
      await isolatedUploadClient.put(uploadUrl, body, {
        headers: {"Content-Type": "application/pdf"},
      });
    });
  }

  static confirmPdf(
    nursingHomeId: string,
    documentId: string,
    body: ConfirmConsentDocumentPdfRequest,
  ) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}/pdf/confirm`,
        body,
      )).data
    );
  }

  static getFinalPdf(documentId: string) {
    return request(async () =>
      (await api.get<ArrayBuffer>(
        `consent-documents/${documentId}/final-pdf`,
        {responseType: "arraybuffer"},
      )).data
    );
  }

  static getAuthorizedPreview(documentId: string) {
    return request(async () => {
      const response = await api.get<ArrayBuffer>(
        `consent-documents/${documentId}/preview`,
        {responseType: "arraybuffer"},
      );
      return {
        bytes: response.data,
        contentType: normalizeConsentPreviewContentType(response.headers["content-type"]),
      } as ConsentDocumentPreview;
    });
  }

  static listAwaitingEmployeeSignature() {
    return request(async () =>
      (await api.get<ConsentDocumentListResponse>(
        "consent-documents/employees/awaiting-signature",
      )).data
    );
  }

  static getEmployeeDocument(documentId: string) {
    return request(async () =>
      (await api.get<ConsentDocument>(
        `consent-documents/employees/${documentId}`,
      )).data
    );
  }

  static signEmployeeDocument(documentId: string, body: EmployeeSignRequest) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `consent-documents/employees/${documentId}/sign`,
        body,
      )).data
    );
  }

  static getResidentPresentation(nursingHomeId: string, documentId: string) {
    return request(async () =>
      (await api.get<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/resident-presentation/${documentId}`,
      )).data
    );
  }

  static verifyResidentIdentity(
    nursingHomeId: string,
    documentId: string,
    body: ResidentIdentityVerificationRequest,
  ) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/resident-presentation/${documentId}/identity-verification`,
        body,
      )).data
    );
  }

  static recordResidentConsent(
    nursingHomeId: string,
    documentId: string,
    body: ResidentConsentRequest,
  ) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/resident-presentation/${documentId}/consent`,
        body,
      )).data
    );
  }

  static recordResidentDecline(nursingHomeId: string, documentId: string) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/resident-presentation/${documentId}/decline`,
      )).data
    );
  }
}
